import { useEffect, useRef, useState } from "react";

import { useScrollLock } from "../useScrollLock";
import { api, ApiError } from "../api";
import { DocumentLink } from "../components/DocumentLink";
import { pogrubienie } from "../components/TekstSchematu";
import { t } from "../i18n";
import type { Datapoint, DocumentPreview, Locale, OnboardingState } from "../types";

interface Props {
  recordId: string;
  datapoint: Datapoint;
  locale: Locale;
  onSigned: (state: OnboardingState) => void;
}

// Akapit umowy bywa obrazem wklejonym w treść jako `![opis](data:image/...)` —
// tak zleceniodawca wstawia podpis drugiej strony. Renderujemy go jako obraz,
// żeby podgląd pokazywał to samo co PDF, a nie ścianę base64. Dopuszczamy tylko
// `data:image/` — inny schemat w <img> i tak by się nie wykonał, ale filtrujemy.
const OBRAZ_AKAPIT = /^!\[([^\]]*)\]\(\s*(data:image\/[A-Za-z0-9.+-]+;base64,[A-Za-z0-9+/=]+)\s*\)$/;

function Akapit({ tekst }: { tekst: string }) {
  const obraz = tekst.trim().match(OBRAZ_AKAPIT);
  if (obraz) {
    return (
      <img
        src={obraz[2]}
        alt={obraz[1] || "podpis"}
        style={{ display: "block", maxWidth: "60mm", maxHeight: "24mm", marginTop: 12 }}
      />
    );
  }
  return <p style={{ marginTop: 12 }}>{pogrubienie(tekst)}</p>;
}


export function SignField({ recordId, datapoint, locale, onSigned }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [drawn, setDrawn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<DocumentPreview | null>(null);

  // Rysowanie w rozdzielczości ekranu — na telefonie inaczej podpis wychodzi rozmyty.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const ratio = window.devicePixelRatio || 1;

      // Zmiana rozmiaru kanwy kasuje rysunek, a `drawn` zostawało włączone —
      // obrót telefonu albo wysunięcie klawiatury zabierało podpis, a przycisk
      // dalej wysyłał. Kandydat podpisywał umowę pustym miejscem i nikt tego
      // nie widział. Przenosimy rysunek na nową kanwę.
      const poprzedni = canvas.width > 0 && canvas.height > 0 ? canvas.toDataURL() : null;

      canvas.width = rect.width * ratio;
      canvas.height = rect.height * ratio;
      const context = canvas.getContext("2d");
      if (!context) return;
      context.scale(ratio, ratio);
      context.lineWidth = 2.2;
      context.lineCap = "round";
      context.lineJoin = "round";
      context.strokeStyle = getComputedStyle(document.body).getPropertyValue("--ink").trim() || "#000";

      if (poprzedni) {
        const obraz = new Image();
        obraz.onload = () => context.drawImage(obraz, 0, 0, rect.width, rect.height);
        obraz.src = poprzedni;
      }
    };

    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [datapoint.key]);

  const positionOf = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  const start = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const context = canvasRef.current?.getContext("2d");
    if (!context) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    const { x, y } = positionOf(event);
    context.beginPath();
    context.moveTo(x, y);
    setDrawn(true);
  };

  const move = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;
    const context = canvasRef.current?.getContext("2d");
    if (!context) return;
    const { x, y } = positionOf(event);
    context.lineTo(x, y);
    context.stroke();
  };

  const wipe = () => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (canvas && context) context.clearRect(0, 0, canvas.width, canvas.height);
    setDrawn(false);
  };

  const openPreview = async () => {
    try {
      setPreview(await api.documentPreview(recordId, datapoint.key, locale));
    } catch (caught) {
      setError((caught as ApiError).message);
    }
  };

  const submit = async () => {
    const canvas = canvasRef.current;
    if (!canvas || !drawn) return;
    setBusy(true);
    setError(null);
    try {
      onSigned(await api.sign(recordId, datapoint.key, canvas.toDataURL("image/png"), locale));
    } catch (caught) {
      setError((caught as ApiError).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <p className="field__sub">{t("signHere", locale)}</p>
      <button className="btn--link" onClick={openPreview}>
        {t("viewDocument", locale)}
      </button>

      <div className="sign" style={{ marginTop: 12 }} data-ybug-sensitive>
        <canvas
          ref={canvasRef}
          className="sign__pad"
          aria-label={datapoint.title}
          onPointerDown={start}
          onPointerMove={move}
        />
        <span className="sign__line" aria-hidden="true" />
        <span className="sign__caption" aria-hidden="true">
          {t("signatureLabel", locale)}
        </span>
      </div>

      {error && <p className="error">{error}</p>}

      <div className="actions">
        <button className="btn btn--secondary" onClick={wipe} disabled={busy}>
          {t("clear", locale)}
        </button>
        <button
          className={`btn btn--primary${busy ? " puls" : ""}`}
          onClick={submit}
          disabled={!drawn || busy}
        >
          {busy ? t("saving", locale) : t("signSave", locale)}
        </button>
      </div>

      {preview && (
        <DocumentModal
          preview={preview}
          locale={locale}
          recordId={recordId}
          docKey={datapoint.key}
          onClose={() => setPreview(null)}
        />
      )}
    </>
  );
}

function DocumentModal({
  preview,
  locale,
  recordId,
  docKey,
  onClose,
}: {
  preview: DocumentPreview;
  locale: Locale;
  recordId: string;
  docKey: string;
  onClose: () => void;
}) {
  useScrollLock();

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="modal" role="dialog" aria-modal="true" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal__card">
        <div className="modal__head">
          <h2 className="modal__title">{preview.title}</h2>
          <DocumentLink recordId={recordId} docKey={docKey} locale={locale} className="btn btn--secondary">
            PDF
          </DocumentLink>
          <button className="btn btn--ghost" onClick={onClose}>
            {t("close", locale)}
          </button>
        </div>

        <div className="modal__body" data-ybug-sensitive>
          {/* Wbudowany dokument jest dwujęzyczny jak PDF: wiążąca wersja polska,
              a pod nią tłumaczenie pomocnicze. Wcześniej podgląd na ekranie gubił
              przekłady wbudowanych dokumentów (renderował tylko prozę z panelu),
              więc kandydat spoza PL widział sam polski i musiał pobierać PDF. */}
          {preview.title_translation && (
            <p className="doc__detail" style={{ marginTop: 0 }}>{preview.title_translation}</p>
          )}
          {preview.disclaimer && !(preview.body && preview.body.length > 0) && (
            <p className="doc__detail">{preview.disclaimer}</p>
          )}

          <dl style={{ margin: 0 }}>
            {preview.rows.map((row) => (
              <div className="doc__row" key={row.label}>
                <dt>
                  {row.label}
                  {row.label_translation && (
                    <span className="doc__detail"> · {row.label_translation}</span>
                  )}
                </dt>
                <dd>{row.value}</dd>
              </div>
            ))}
          </dl>

          {preview.intro && <p style={{ marginTop: 18 }}>{preview.intro}</p>}
          {preview.intro_translation && <p className="doc__detail">{preview.intro_translation}</p>}

          {/* Dokument pisany w panelu: zamiast punktów TAK/NIE ma zwykłe akapity
              z podstawionymi wartościami — człowiek czyta to, co podpisuje. */}
          {preview.body?.map((paragraph, position) => (
            <Akapit key={position} tekst={paragraph} />
          ))}
          {preview.body_translation && preview.body_translation.length > 0 && (
            <div className="doc__translation">
              {preview.disclaimer && <p className="doc__detail">{preview.disclaimer}</p>}
              {preview.body_translation.map((paragraph, position) => (
                <p key={position}>{paragraph}</p>
              ))}
            </div>
          )}

          <ol className="doc__claims">
            {preview.claims.map((claim) => (
              <li key={claim.text}>
                <span className={`doc__state doc__state--${claim.state}`}>
                  {claim.state === "na" ? t("notApplicable", locale) : claim.state.toUpperCase()}
                </span>
                {claim.text}
                {claim.translation && <span className="doc__detail">{claim.translation}</span>}
                {claim.detail && <span className="doc__detail">{claim.detail}</span>}
              </li>
            ))}
          </ol>

          {preview.closing && <p style={{ marginTop: 18, color: "var(--muted)" }}>{preview.closing}</p>}
          {preview.closing_translation && (
            <p className="doc__detail" style={{ marginTop: 4 }}>{preview.closing_translation}</p>
          )}
        </div>
      </div>
    </div>
  );
}
